import { askBeforeLeavingUntil } from '@lib/customization/unsavedWork';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The last write queued under each name, while it is still on its way. A page that is left
 * unmounts, but what it had sent goes on, and whoever opens the same thing again before that
 * has ended must not start from what the unfinished write is about to change.
 */
const onTheirWay = new Map<string, Promise<void>>();

/**
 * Runs an editor's writes one after another, in the order they were asked for. What it
 * answers queues a write and settles with that write's own outcome; a write that fails does
 * not hold back the ones behind it.
 *
 * For writes that lean on each other: an upload has to be through before the save that names
 * its image, and a write that waited its turn has to read the draft as it is when it runs,
 * not as it was when it was asked for.
 *
 * `name` says what is being written, for `useWritesLeftBehind`. Until a write has ended a
 * reload or a close of the tab asks first, also once the editor that queued it has been left:
 * the tab going would cut it off, and a save that waits behind an upload would never be sent.
 */
export const useWriteChain = (name: string) => {
  const chain = useRef<Promise<void>>(Promise.resolve());

  return useCallback(<T>(run: () => Promise<T>): Promise<T> => {
    const result = chain.current.then(run, run);
    const settled = result.then(() => undefined, () => undefined);

    chain.current = settled;
    onTheirWay.set(name, settled);
    askBeforeLeavingUntil(settled);

    void settled.then(() => {
      if (onTheirWay.get(name) === settled) onTheirWay.delete(name);
    });

    return result;
  }, [name]);
};

/**
 * Whether writes queued under a name by an earlier visit are still on their way. True until
 * the last of them has ended; an editor waits that long before it takes up the draft that
 * visit left, which those writes are still changing: a save that lands moves the version the
 * draft is based on, and an upload puts its image into it.
 *
 * Asked once, when the component mounts, and of what was on its way at that moment: the
 * writes the editor then makes itself are not waited for. So it belongs in a component that
 * is mounted afresh for each thing it names.
 */
export const useWritesLeftBehind = (name: string): boolean => {
  const [waitingFor, setWaitingFor] = useState(() => onTheirWay.get(name));

  useEffect(() => {
    if (!waitingFor) return undefined;

    let mounted = true;

    // The visit that is gone may have queued another behind the one that has just ended.
    void waitingFor.then(() => {
      if (mounted) setWaitingFor(onTheirWay.get(name));
    });

    return () => {
      mounted = false;
    };
  }, [waitingFor, name]);

  return waitingFor !== undefined;
};
